import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import StyledDialog from 'shared/elements/StyledDialog/StyledDialog';
import IMedia from 'shared/interfaces/IMedia';
import {
  IAgencyTalentPermissionPayload,
  IProfileMedia,
  IProfileMediaSetSelectPayload,
} from 'shared/interfaces/IProfile';
import { IOnProgress } from 'shared/interfaces/utils/IAxios';
import { mediaService } from 'shared/services/mediaService';
import { profileService } from 'shared/services/profileService';
import { uploadService } from 'shared/services/uploadService';
import { v4 as uuidv4 } from 'uuid';
import { SectionCont } from '../image-video-audio.styles';
import AddModal from '../Modals/AddModal';
import DocumentContent from './DocumentContent/DocumentContent';
import DocumentHeader from './DocumentHeader/DocumentHeader';

interface Props {
  fetching: boolean;
  handleAgencyTalentPermissions: (payload: IAgencyTalentPermissionPayload) => void;
  permissions?: string[];
  nonRepre: boolean;
}

interface IOutputE {
  file: File;
  id: string;
}

const { getMediaProfile, setSelectProfileMedia, unSelectProfileMedia } = profileService();
const { getMediaList } = mediaService();
const { uploadMedia } = uploadService();

const Documents = ({ fetching, handleAgencyTalentPermissions, permissions, nonRepre }: Props) => {
  // Check if Talent is Allowed to Customize the Document Data
  const isAllowed = Boolean(permissions?.includes('Documents')) || nonRepre;

  // Query Client
  const queryClient = useQueryClient();

  // Documents to be uploaded
  const [toUpload, setToUpload] = useState<IOutputE[]>([]);

  // Add Modal
  const [addModal, setAddModal] = useState(false);

  // Profile ID
  const { profileId } = useParams() as { profileId: string };

  // For Mutating Data
  const { mutate, mutateAsync } = setSelectProfileMedia();
  const { mutate: unselectMutate } = unSelectProfileMedia();

  // This is the Data of Documents that is shown for Specific Profile
  const { data: profileDocumentData } = getMediaProfile(profileId, {
    file_type: 'document',
  });

  // Upload Media
  const { mutateAsync: uploadMediaMutateAsync } = uploadMedia();

  // All the Data Documents of talent
  const { data: talentDocumentData } = getMediaList({
    file_type: 'document',
    media_bin: true,
  });

  // This is the Document Data that is shown For Specific Profile
  const specificProfileDocumentData = useMemo(() => profileDocumentData?.data || [], [profileDocumentData]);

  // All the Data Documents of talent
  const allDocuments = useMemo(() => talentDocumentData?.data || [], [talentDocumentData]);

  // Filtered Documents List, the allDocuments is the one being filtered here using the specific Profile Document Data
  // So the Types for this is the "IMedia"
  const hiddenDocumentData: IMedia[] = useMemo(
    () =>
      allDocuments?.filter(
        (docs) => !specificProfileDocumentData?.some((docsData) => docsData?.attributes?.medium_id === docs.id),
      ) || [],
    [specificProfileDocumentData, allDocuments],
  );

  // Setup the Document to be uploaded
  const handleOutput = (docs: File[]) => {
    const mDocs = docs.map((a) => ({ file: a, id: uuidv4() }));
    setToUpload(mDocs);
  };

  // Handling open of modal
  const handleAddModal = useCallback(() => {
    setAddModal((prev) => !prev);
  }, []);

  const [uploadingProgress, setUploadingProgress] = useState<number | null>(null);

  const onProgres: IOnProgress = (progress: number) => {
    setUploadingProgress(progress);
  };

  const handleUpload = useCallback(async () => {
    await Promise.all(
      toUpload?.map(async (data) => {
        const formData = new FormData();
        const image = data.file;
        formData.set('attachment', image);
        formData.append('file_name', image.name);
        formData.append('file_type', 'document');
        formData.set('file_size', image?.size?.toString());
        formData.append('media_bin', 'true');

        const uploadedPhoto = await uploadMediaMutateAsync({ formData, onProgres });
        const payload: IProfileMediaSetSelectPayload = {
          id: uploadedPhoto.data.id,
          tag_list: 'document',
        };

        await mutateAsync({
          profileId,
          payload: {
            ...payload,
            sort: specificProfileDocumentData ? specificProfileDocumentData?.length : 0,
          },
        });
      }),
    );
    queryClient.invalidateQueries(['profile_media', profileId], {
      refetchInactive: true,
    });
    queryClient.invalidateQueries(['media'], {
      refetchInactive: true,
    });
    queryClient.invalidateQueries(['profile', profileId], {
      refetchInactive: true,
    });
    setToUpload([]);
    setUploadingProgress(null);
  }, [specificProfileDocumentData, toUpload]);

  const handleUnselectMedia = useCallback((profileMediaId: string) => {
    unselectMutate(
      { profileId, profileMediaId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['profile_media', profileId], {
            refetchInactive: true,
          });
        },
      },
    );
  }, []);

  const handleSetSelectMedia = useCallback(
    (payload: IProfileMediaSetSelectPayload) => {
      mutate(
        {
          profileId,
          payload: { ...payload, sort: specificProfileDocumentData ? specificProfileDocumentData.length : 0 },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['profile_media', profileId], {
              refetchInactive: true,
            });
          },
        },
      );
    },

    [profileId, specificProfileDocumentData],
  );

  const handleCheck = useCallback(
    (a: Partial<IMedia | IProfileMedia>) => {
      if (specificProfileDocumentData.find((docs) => docs.id === a.id)) {
        handleUnselectMedia(a.id as string);
      } else {
        handleSetSelectMedia({
          id: a.id as string,
          tag_list: 'document',
        });
      }
    },

    [specificProfileDocumentData],
  );

  useEffect(() => {
    if (toUpload?.length > 0 && !uploadingProgress) {
      handleUpload();
    }
  }, [handleUpload, toUpload, uploadingProgress]);

  return (
    <SectionCont>
      <Accordion defaultExpanded style={{ backgroundColor: 'transparent', margin: 0 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
          <DocumentHeader
            documentShownLength={specificProfileDocumentData?.length || 0}
            allDocumentLength={allDocuments?.length || 0}
            permissions={permissions}
            nonRepre={nonRepre}
            handleAddModal={handleAddModal}
          />
        </AccordionSummary>
        <AccordionDetails>
          <DocumentContent
            profileId={profileId}
            isAllowed={isAllowed}
            fetching={fetching}
            hiddenDocumentData={hiddenDocumentData}
            specificProfileDocumentData={specificProfileDocumentData}
            toUpload={toUpload}
            uploadingProgress={uploadingProgress}
            handleCheck={handleCheck}
            handleAgencyTalentPermissions={handleAgencyTalentPermissions}
          />
        </AccordionDetails>
      </Accordion>
      <StyledDialog
        style={{ zIndex: 9999 }}
        open={addModal}
        onClose={handleAddModal}
        maxWidth="md"
        aria-labelledby="simple-dialog-title"
      >
        <AddModal close={handleAddModal} type="Documents" output={handleOutput} />
      </StyledDialog>
    </SectionCont>
  );
};

export default Documents;
